import moment from 'moment'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import BusyOverlay from 'components/common/BusyOverlay'
import Button, { LinkButton } from 'components/common/Button'
import CardWrapper from 'components/dashboard/CardWrapper'
import { THIS_MONTH, THIS_WEEK } from 'components/dashboard/DashboardCardHeader'
import Icon from 'components/Icon'
import { getScheduleMaxRuns } from 'components/scenarios/utils'
import { ScheduledAttackRow } from 'components/schedules/AttacksScheduleList'
import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import theme from 'theme'

import EmptyCard from './EmptyCard'

// this is explicitly separated from the component below so we can control the state in storybook examples
// and it also allows us to test the UI portion without having to mock the api calls
export function ScheduledExperiments({
  schedules = [],
  loading,
  error,
  doFetch
}) {
  const history = useHistory()

  if (loading) {
    return <BusyOverlay rounded shown />
  } else if (schedules.length === 0 && !error) {
    return (
      <EmptyCard title="No scheduled experiments">
        <LinkButton label="Schedule an experiment" to="/schedules/attacks/" />
      </EmptyCard>
    )
  } else if (error) {
    return (
      <EmptyCard
        title="There was an error loading schedules"
        titleColor={theme.colors.red[0]}
      >
        <Button label="Refresh" onClick={() => doFetch()} />
      </EmptyCard>
    )
  }
  return (
    <div>
      {schedules.map((schedule, i) => {
        return (
          <div
            key={i}
            data-table-scheduled-row
            css={{
              display: 'grid',
              gridTemplateColumns:
                '42px minmax(48px, 1fr) minmax(48px, 1fr) 42px',
              alignItems: 'center',
              gap: theme.space[3],
              padding: theme.space[3],
              color: theme.colors.steel[1],
              borderBottom: theme.border.base,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme.colors.panel[0]
              }
            }}
            onClick={() => history.push('/schedules/attacks')}
          >
            <ScheduledAttackRow
              isBasic
              doFetch={doFetch}
              maxRuns={getScheduleMaxRuns(schedule)}
              schedule={schedule}
              size="small"
            />
            <Icon
              css={{
                marginLeft: theme.space[2],
                color: theme.colors.steel[2]
              }}
              icon="chevronRight"
            />
          </div>
        )
      })}
    </div>
  )
}

ScheduledExperiments.propTypes = {
  schedules: PropTypes.array,
  error: PropTypes.object,
  doFetch: PropTypes.func,
  loading: PropTypes.bool
}

export function getSchedulesByTimeline(schedules, timeline) {
  if (!Array.isArray(schedules) || schedules.length < 1) {
    return []
  }

  let startTS, endTS

  switch (timeline) {
    case THIS_MONTH:
      startTS = moment().date(1).unix()
      endTS = moment.unix(startTS).endOf('month').unix()
      break

    // this week
    default:
    case THIS_WEEK:
      startTS = moment().day(1).unix()
      endTS = moment.unix(startTS).endOf('week').unix()
  }

  return schedules.filter((schedule) => {
    if (schedule.trigger.type === 'Once') {
      const runTS = moment(schedule.trigger.exact).unix()
      return runTS >= startTS && runTS <= endTS
    }

    // random trigger, occurs regularly on chosen days, so will always run every week/month
    return true
  })
}

export function ScheduledExperimentsDataCard({
  schedules,
  error,
  doFetch,
  filter
}) {
  const scheduleList = useMemo(
    () => getSchedulesByTimeline(schedules, filter),
    [schedules, filter]
  )

  return (
    <ScheduledExperiments
      doFetch={doFetch}
      error={error}
      schedules={scheduleList}
    />
  )
}

ScheduledExperimentsDataCard.propTypes = {
  schedules: PropTypes.array,
  error: PropTypes.object,
  doFetch: PropTypes.func,
  filter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default function ScheduledExperimentsCard() {
  const { currentTeamId } = useAuth()

  const {
    data: schedules,
    // schedules load is critical, metadata is secondary
    loading: isLoading,
    error: error,
    doFetch
  } = useAgent(`/schedules/active?teamId=${currentTeamId}`, [], {
    shouldCache: true
  })

  return (
    <CardWrapper
      filters={[
        { value: THIS_WEEK, label: 'This week' },
        { value: THIS_MONTH, label: 'This month' }
      ]}
      hideFilter={isLoading}
      renderCardContent={({ filter }) => {
        return isLoading ? (
          <BusyOverlay rounded shown />
        ) : (
          <ScheduledExperimentsDataCard
            doFetch={doFetch}
            error={error}
            filter={filter}
            schedules={schedules}
          />
        )
      }}
      title="Scheduled Experiments"
    />
  )
}
